import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    IconButton,
    Button,
    CircularProgress,
    useMediaQuery,
    useTheme,
    alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

/**
 * FormDialog — a reusable premium dialog shell matching the Arbor & Vault design system.
 *
 * Props:
 *  open          boolean       — controls visibility
 *  onClose       fn            — called on close/cancel
 *  onSubmit      fn            — called when form is submitted
 *  title         string        — dialog heading
 *  subtitle      string?       — optional description line under heading
 *  saveLabel     string?       — label for the save button (default: "Save")
 *  saving        boolean?      — disables save button and shows spinner
 *  saveDisabled  boolean?      — additionally disables save button
 *  testId        string?       — data-testid on the Dialog root
 *  maxWidth      string?       — MUI maxWidth (default: "sm")
 *  children      ReactNode     — form content
 */
// Arbor & Vault Design Tokens mapped to Emerald Green & Canvas background
export const arborGreen = '#006D43';
export const creamBg = '#F7F9FB';

export const filledSelectStyle = {
    width: '100%',
    height: '56px',
    paddingLeft: '16px',
    paddingRight: '48px',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: 'rgba(10, 42, 33, 0.05)',
    fontSize: '1rem',
    fontWeight: 600,
    color: arborGreen,
    cursor: 'pointer',
    outline: 'none',
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23006D43' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 20px center',
    backgroundSize: '18px 18px',
    boxSizing: 'border-box',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
};

export const filledInputSx = {
    '& .MuiFilledInput-root': {
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
        borderRadius: (theme) => `${theme.shape?.inputRadius || 12}px`,
        overflow: 'hidden',
        height: (theme) => theme.shape?.inputHeight || 56,
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08) },
        '&.Mui-focused': { 
            bgcolor: '#fff',
            boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}`
        },
        '&:before, &:after': { display: 'none' },
        display: 'flex',
        alignItems: 'center',
        '&.MuiInputBase-multiline': {
            height: 'auto',
            minHeight: (theme) => theme.shape?.inputHeight || 56,
            alignItems: 'flex-start',
            py: 1.5,
        },
    },
    '& .MuiFilledInput-input': {
        py: 0,
        px: 2,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        fontSize: '0.95rem',
        color: (theme) => theme.palette.text.primary,
        fontWeight: 500,
        borderRadius: 'inherit',
    },
    '& .MuiInputBase-inputMultiline': {
        height: 'auto !important',
        display: 'block !important',
        lineHeight: 1.5,
        py: '0 !important',
    },
    '& .MuiSelect-select': {
        display: 'flex',
        alignItems: 'center',
        pr: '48px !important',
        height: '100%',
        boxSizing: 'border-box',
    },
    '& .MuiSelect-icon': {
        right: '20px',
        fontSize: '1.5rem',
        color: (theme) => alpha(theme.palette.text.primary, 0.6),
    },
    '& input:-webkit-autofill': {
        borderRadius: 'inherit',
    },
    '& .MuiAutocomplete-inputRoot': {
        paddingTop: '0 !important',
        paddingBottom: '0 !important',
    },
    '& .MuiAutocomplete-input': {
        padding: '0 !important',
        height: '100%',
    },
    '& .MuiInputBase-input::placeholder': {
        color: (theme) => alpha(theme.palette.text.primary, 0.4),
        opacity: 1,
    },
    '& .MuiInputAdornment-root': {
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        marginTop: '0 !important',
    },
    // Fix for MuiTelInput weird country icon
    '& .MuiTelInput-IconButton': {
        backgroundColor: 'transparent !important',
        padding: '0 8px 0 16px',
        borderRadius: '0px',
        '&:hover': { backgroundColor: 'transparent !important' },
    },
    '& .MuiOutlinedInput-root': {
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
        borderRadius: (theme) => `${theme.shape?.inputRadius || 12}px`,
        overflow: 'hidden',
        '& fieldset': { border: 'none' },
        '&:hover fieldset': { border: 'none' },
        '&.Mui-focused fieldset': { border: 'none' },
        '&.Mui-focused': { 
            bgcolor: '#fff',
            boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}`
        },
    },
    '& .MuiOutlinedInput-input': {
        borderRadius: 'inherit',
    }
};

const FormDialog = ({
    open,
    onClose,
    onSubmit,
    title,
    subtitle,
    saveLabel,
    cancelLabel,
    saving = false,
    saveDisabled = false,
    testId,
    maxWidth = 'sm',
    autoFocusFirst = true,
    TransitionProps,
    children,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const formRef = React.useRef(null);

    const focusFirstInput = React.useCallback(() => {
        if (!autoFocusFirst || !formRef.current) return;
        const form = formRef.current;
        const active = document.activeElement;
        // If an editable field inside form is already focused, keep it
        if (active && form.contains(active) && ['INPUT', 'SELECT', 'TEXTAREA'].includes(active.tagName)) {
            return;
        }
        const focusable = form.querySelector(
            'input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [role="combobox"]:not([aria-disabled="true"])'
        );
        if (focusable && typeof focusable.focus === 'function') {
            focusable.focus();
        }
    }, [autoFocusFirst]);

    const handleEntered = (node, isAppearing) => {
        TransitionProps?.onEntered?.(node, isAppearing);
        focusFirstInput();
    };

    React.useEffect(() => {
        if (open && autoFocusFirst) {
            const timer = setTimeout(() => {
                focusFirstInput();
            }, 80);
            return () => clearTimeout(timer);
        }
    }, [open, autoFocusFirst, focusFirstInput]);

    const resolvedSaveLabel = saveLabel !== undefined ? saveLabel : t('Save');
    const resolvedCancelLabel = cancelLabel !== undefined ? cancelLabel : t('Cancel');

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={maxWidth}
            fullWidth
            fullScreen={isMobile}
            data-testid={testId}
            TransitionProps={{
                ...TransitionProps,
                onEntered: handleEntered,
            }}
            PaperProps={{
                sx: {
                    bgcolor: 'background.default', // Use theme canvas background
                    backgroundImage: 'none',
                    // Let the theme handle the base radius (32px), 
                    // but ensure it's 0 on mobile.
                    ...(isMobile && { borderRadius: 0 }),
                    boxShadow: (theme) => `0 40px 100px ${alpha(theme.palette.primary.main, 0.15)}`, // Theme primary main shadow tint
                    overflow: 'hidden',
                },
            }}
        >
        <form 
            ref={formRef}
            onSubmit={(e) => { e.preventDefault(); onSubmit?.(); }}
            style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                maxHeight: 'inherit', // Inherit the Dialog's Paper max-height
                minHeight: 0,         // Crucial for flex child scrolling
                overflow: 'hidden'    // Contain children
            }}
        >
                {/* Header */}
                <DialogTitle
                    component="div"
                    sx={{
                        px: 4,
                        pt: 4,
                        pb: subtitle ? 1 : 2,
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 600,
                                fontFamily: 'Manrope, sans-serif',
                                letterSpacing: '-0.02em',
                                color: 'primary.main',
                                lineHeight: 1.1,
                            }}
                        >
                            {title}
                        </Typography>
                        {subtitle && (
                            <Typography
                                variant="body2"
                                sx={{ 
                                    mt: 1, 
                                    fontWeight: 400, 
                                    color: 'text.secondary',
                                    maxWidth: '90%'
                                }}
                            >
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{
                            mt: -0.5,
                            mr: -1,
                            color: 'primary.main',
                            opacity: 0.5,
                            '&:hover': { opacity: 1, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05) },
                        }}
                        aria-label={t("Close")}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
 
                {/* Content */}
                <DialogContent sx={{ px: 4, py: 2, flex: 1, minHeight: 0, overflowY: 'auto' }}>
                    {children}
                </DialogContent>
 
                {/* Actions */}
                <Box sx={{ p: 1 }}>
                    <DialogActions sx={{ px: 3, py: 2, gap: 1.5 }}>
                        <Button
                            onClick={onClose}
                            variant="text"
                            sx={{
                                fontWeight: 600,
                                color: 'primary.main',
                                opacity: 0.7,
                                textTransform: 'none',
                                '&:hover': { opacity: 1, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05) },
                            }}
                        >
                            {resolvedCancelLabel}
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={saveDisabled || saving}
                            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                            disableElevation
                            sx={{
                                fontWeight: 700,
                                px: 4,
                                py: 1,
                                // Inherits borderRadius from theme.root
                                textTransform: 'none',
                                bgcolor: 'primary.main',
                                color: '#fff',
                                '&:hover': { bgcolor: 'primary.dark' },
                                '&.Mui-disabled': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1) }
                            }}
                        >
                            {saving ? t('Processing…') : resolvedSaveLabel}
                        </Button>
                    </DialogActions>
                </Box>
            </form>
        </Dialog>
    );
};
 
/**
 * FormField — a labeled form field wrapper matching the Arbor & Vault input style.
 */
export const FormField = ({ label, id, required = false, children, sx = {} }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ...sx }}>
        <Typography
            component="label"
            htmlFor={id}
            variant="caption"
            sx={{
                fontWeight: 600,
                color: 'text.secondary',
                fontSize: '0.85rem',
                letterSpacing: '-0.01em',
                cursor: 'pointer',
                display: 'block'
            }}
        >
            {label}
            {required && (
                <Typography component="span" color="error.main" sx={{ ml: 0.5 }}>*</Typography>
            )}
        </Typography>
        {children}
    </Box>
);

/**
 * FormSection — a visual grouping of related fields with an optional section title.
 */
export const FormSection = ({ title, children, sx = {} }) => (
    <Box sx={{ ...sx }}>
        {title && (
            <Typography
                variant="overline"
                sx={{
                    display: 'block',
                    mb: 1.5,
                    mt: 0.5,
                }}
            >
                {title}
            </Typography>
        )}
        {children}
    </Box>
);

export default FormDialog;
